import { useEffect, useRef } from 'react'

/**
 * Hook to handle browser back navigation for drawers in PWA
 *
 * When a drawer opens, it pushes a history entry so that:
 * - Browser back button closes the drawer instead of leaving the page
 * - Android hardware back button closes the drawer
 * - iOS swipe-back gesture closes the drawer
 *
 * Usage:
 * ```tsx
 * const [open, setOpen] = useState(false);
 * const { markHistoryForOpen, closeAndGoBack } = useDrawerBackBehavior({
 *   isOpen: open,
 *   onClose: () => setOpen(false),
 * });
 *
 * // When opening drawer
 * const handleOpen = () => {
 *   markHistoryForOpen();
 *   setOpen(true);
 * };
 *
 * // Use closeAndGoBack for onClose prop
 * <Drawer open={open} onClose={closeAndGoBack} />
 * ```
 */
export function useDrawerBackBehavior({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
    const pushedRef = useRef(false)
    const previousOpenRef = useRef(isOpen)
    const isBackNavigationRef = useRef(false)
    const drawerIdRef = useRef<string>(Math.random().toString(36).substr(2, 9))

    useEffect(() => {
        const onPop = (event: PopStateEvent) => {
            // Only handle popstate if this drawer is open and has pushed history
            if (isOpen && pushedRef.current) {
                // Check if the popstate event is for this specific drawer
                const state = event.state
                if (state && state.drawerId === drawerIdRef.current) {
                    isBackNavigationRef.current = true
                    pushedRef.current = false
                    onClose()
                }
            }
        }

        window.addEventListener('popstate', onPop)
        return () => window.removeEventListener('popstate', onPop)
    }, [isOpen, onClose])

    // Auto-detect when drawer opens and mark history
    useEffect(() => {
        if (isOpen && !previousOpenRef.current && !pushedRef.current) {
            // Drawer just opened, mark history with unique drawer ID
            window.history.pushState({ drawer: true, drawerId: drawerIdRef.current }, '')
            pushedRef.current = true
        }
        previousOpenRef.current = isOpen
    }, [isOpen])

    // Clean up history entry when drawer closes (for non-back navigation closes)
    useEffect(() => {
        if (!isOpen && pushedRef.current && !isBackNavigationRef.current) {
            // Drawer closed manually (backdrop/cross), remove the history entry
            pushedRef.current = false
            // Use history.back() to actually remove the entry from the stack
            window.history.back()
        }

        // Reset the back navigation flag when drawer closes
        if (!isOpen) {
            isBackNavigationRef.current = false
        }
    }, [isOpen])

    /**
     * Call this when opening the drawer.
     * IMPORTANT: Must be called inside the same click/tap event that opens the drawer
     * for iOS compatibility.
     */
    function markHistoryForOpen() {
        if (!pushedRef.current) {
            window.history.pushState({ drawer: true, drawerId: drawerIdRef.current }, '')
            pushedRef.current = true
        }
    }

    /**
     * Simple close function that just calls onClose.
     * The cleanup of history entry is handled automatically in useEffect.
     */
    function closeAndGoBack() {
        onClose()
    }

    return { markHistoryForOpen, closeAndGoBack }
}

export default useDrawerBackBehavior
